import React from 'react';
import { Box, Divider, Grid } from '@mui/material';
import { PAGE_TEXT } from '../../lang';
import { type ApplicantInfoFormValues } from '../..';
import { Accordion, LabelValue } from '../../../../../components';
import { PROGRAMME_TYPE } from '../../../../Application/components';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';

const APPLICANT_TEXT = PAGE_TEXT.CHIEF_SUPERVISOR.APPLICANT_INFORMATION;
const applicationInfoForm = [
  { label: APPLICANT_TEXT.APPLICATION_NO, name: 'applicationNumber' },
  { label: APPLICANT_TEXT.APPLICANT_NAME, name: 'applicantName' },
  { label: APPLICANT_TEXT.ENTRY_YEAR, name: 'entryYear' },
  { label: APPLICANT_TEXT.PROGRAMME_TYPE, name: 'programmeType' },
  { label: APPLICANT_TEXT.PROGRAMME, name: 'programme' },
  { label: APPLICANT_TEXT.DEPARTMENT, name: 'department' },
  {
    label: APPLICANT_TEXT.PARTNER_INSTITUTION,
    name: 'partnerInstitution',
    type: 'partner',
  },
  {
    label: APPLICANT_TEXT.CHIEF_SUPERVISOR_OF_PARTNER_INSTITUTION,
    name: 'chiefSupervisorOfPartnerInstitution',
    type: 'partner',
  },
  { label: APPLICANT_TEXT.RESEARCH_AREA, name: 'researchArea' },
  { label: APPLICANT_TEXT.PROPOSED_SUPERVISOR, name: 'proposedSupervisor' },
  { label: APPLICANT_TEXT.RESEARCH_AREA_OPTIONAL, name: 'researchAreaBackup' },
  {
    label: APPLICANT_TEXT.PROPOSED_CHIEF_SUPERVISOR_OPTIONAL,
    name: 'proposedChiefSupervisorBackup',
  },
];

const checkIsPartnerPartShown = (
  programmeType: string | undefined,
): boolean => {
  return (
    programmeType === PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME ||
    programmeType === PROGRAMME_TYPE.COLLABORATIVE_PHD_DEGREE_PROGRAMME
  );
};

type ApplicantInformationBoxProps = {
  applicantInfoValues: ApplicantInfoFormValues;
};

export const ApplicantInformationBox: React.FunctionComponent<
  ApplicantInformationBoxProps
> = props => {
  const { applicantInfoValues } = props;
  const hasNominated =
    applicantInfoValues.status ===
      APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED ||
    applicantInfoValues.status === APPLICATION_STATUS_TYPE.SUBMITTED;
  const subTitleName = hasNominated
    ? APPLICANT_TEXT.TITLE + ' (' + applicantInfoValues.status + ')'
    : APPLICANT_TEXT.TITLE;

  const processText = (text: string): string => {
    return text
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, match => match.toUpperCase());
  };

  if (applicantInfoValues.programme) {
    applicantInfoValues.programmeType = processText(
      applicantInfoValues.programmeType,
    ).replace(/Phd/g, 'PhD');
    applicantInfoValues.programme = processText(
      applicantInfoValues.programme,
    ).replace(/Of/g, 'of');
  }
  const programmeType = applicantInfoValues.programmeType;
  const isPartnerPartShown = checkIsPartnerPartShown(programmeType);

  const initialForm = isPartnerPartShown
    ? applicationInfoForm
    : applicationInfoForm.filter(item => item.type !== 'partner');

  const renderData = [];

  for (let i = 0; i < initialForm.length; i += 2) {
    const firstColumn = initialForm[i];
    const secondColumn = initialForm[i + 1];

    renderData.push(
      <React.Fragment key={i}>
        <Grid container spacing={2} padding={1}>
          <Grid size={7}>
            <LabelValue
              label={firstColumn.label}
              value={
                applicantInfoValues[
                  firstColumn.name as keyof ApplicantInfoFormValues
                ]
              }
            />
          </Grid>
          {secondColumn !== undefined && (
            <Grid size={5}>
              <LabelValue
                label={secondColumn.label}
                value={
                  applicantInfoValues[
                    secondColumn.name as keyof ApplicantInfoFormValues
                  ]
                }
              />
            </Grid>
          )}
        </Grid>
        <Divider />
      </React.Fragment>,
    );
  }

  return (
    <Box sx={{ width: '100%' }} marginBottom={5}>
      <Accordion
        style={{
          width: '100%',
        }}
        title={subTitleName}
        defaultExpanded
        disableExpand
        details={<Box padding={2}>{renderData}</Box>}
      />
    </Box>
  );
};
